import React, {useCallback, useEffect, useRef, useState} from 'react'
import {Button, Offcanvas} from 'react-bootstrap'
import RecipeList from '../../recipe/list/RecipeList'
import {IRecipe} from '../../../api/IRecipe'
import {getRecipes} from '../../../api/api'
import RecipeCardTiny from "./RecipeCardTiny";
import {Grid} from "@mui/material";
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import {useTranslation} from 'react-i18next';
import RecipeSearchBar from '../../../components/RecipeSearchBar/RecipeSearchBar';

interface IStepOneProps {
  selection: IRecipe[]
  setSelection: (l: IRecipe[]) => void
}
const StepOne = ({ selection, setSelection}: IStepOneProps): JSX.Element => {
  const { t } = useTranslation();
  const [recipes, setRecipes] = useState<{list:IRecipe[], next: string}>({list: [], next: ""})
  const [error, setError] = useState(null)
  const [page, setPage] = useState(1)
  const [refresh, setRefresh] = useState(false)
  const loader = useRef(null);
  const fetching = useRef(false);
  const [showSelectedList, setShowSelectedList] = useState(selection.length > 0 ? true : false)
  const [searchResults, setSearchResults] = useState<IRecipe[] | null>(null)

  const isSearchActive = searchResults !== null;
  const displayRecipes = isSearchActive ? searchResults : recipes.list;

  const handleSearchResults = useCallback((results: IRecipe[] | null) => {
    setSearchResults(results);
  }, []);

  const fetch = async() => {
    try {
      fetching.current = true
      if(recipes.next == null) {
        return
      }
      let limit = 26
      const mediaQueryMedium = window.matchMedia("(max-width: 768px)")
      if (mediaQueryMedium.matches) {
        limit = 12
      }
      const mediaQueryXS = window.matchMedia("(max-width: 576px)")
      if (mediaQueryXS.matches) {
        limit = 12
      }
      const nextRecipes = await getRecipes(recipes.next, limit, false)
      const newList = recipes.list.concat(nextRecipes.recipes)
      setRecipes(() => {
        return {list: newList, next: nextRecipes.next}
      })
    } catch (err) {
      setError(err)
    } finally {
      fetching.current = false
    }
  }

  const handleObserver = (entities) => {
    const target = entities[0];
    if (target.isIntersecting) {
      setPage((p) => p + 1)
    }
  }
  useEffect(() => {
    if (selection?.length > 0) {
      setShowSelectedList( true)
    }
  }, [selection])

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "20px",
      threshold: 1.0
    };
    const observer = new IntersectionObserver(handleObserver, options);
    if (loader.current) {
      observer.observe(loader.current)
    }
  }, []);

  useEffect(() => {
      setRecipes(() => {
        return {list: [], next: ""}
      })
      setPage(() => {return 1})
      setRefresh(false)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refresh])

  useEffect(() => {
      if (fetching.current) {
        return
      }
      fetch()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page])


  if (!recipes && !error) {
    return <>{t('common.loading')}</>
  }

  const onSelect = (r: IRecipe, add): void => {
    const idx = selection.findIndex(re => re.id === r.id)
    if (add) {
      setSelection([r, ...selection])
    } else {
      setSelection([...selection.slice(0, idx), ...selection.slice(idx+1)])
    }
  }

  return (
    <div className="mt-md-3">
      <RecipeSearchBar onSearchResults={handleSearchResults} />

      <div className={"footer-sticky"}>
        <div className="icon">
          <Button variant="primary"
                  className={"position-fixed bottom-0 end-0 me-2 mb-2 high-z-index"}
                  onClick={() => setShowSelectedList(!showSelectedList)}>
            {showSelectedList && <KeyboardDoubleArrowDownIcon/>}
            {!showSelectedList && <KeyboardDoubleArrowUpIcon/>}
          </Button>
        </div>
        <Offcanvas
          className="offcanvas-transparent"
          show={showSelectedList}
          placement={"bottom"}
          scroll={true}
          backdrop={false}
          onHide={() => setShowSelectedList(false)}>
          <Offcanvas.Body>
            <div className={"d-flex overflow-scroll"}>
              <Button variant="primary"
                      className={"position-fixed bottom-0 end-0 me-2 mb-2 high-z-index"}
                      onClick={() => setShowSelectedList(!showSelectedList)}>
                {showSelectedList && <KeyboardDoubleArrowDownIcon/>}
                {!showSelectedList && <KeyboardDoubleArrowUpIcon/>}
              </Button>
            {selection && selection.length > 0 && selection.map((r: IRecipe, idx: number) => (
              <RecipeCardTiny
                key={idx}
                className="card-fixed-size recipe-card-title me-1"
                recipe={r}
                onDeselect={(recip) => onSelect(recip, false)}
              />
              ))}
            {!selection || selection.length === 0 && <>{t('batch.emptySelection')}</>}
            </div>
          </Offcanvas.Body>
        </Offcanvas>
      </div>
      <>
        <Grid container spacing={{xs: 1}}>
          <RecipeList
            onSelect={(r) => onSelect(r, true)}
            onDeselect={(r) => onSelect(r, false)}
            selection={selection}
            recipes={displayRecipes}
            setError={setError}
            setRefresh={setRefresh}
          />
        </Grid>
        {!isSearchActive && (
          <div className="loading" ref={loader}>
            {recipes.next != null  && (<h3>{t('common.moreItems', {items: t('navigation.recipes').toLowerCase()})}</h3>)}
          </div>
        )}
      </>
    </div>
  )
}
export default StepOne
